import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { GalleryGrid } from '../components/GalleryGrid';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { SectionHeading } from '../components/SectionHeading';
import { CtaSection } from '../components/CtaSection';
import { featuredProjects, galleryProjects } from '../data/gallery';
import { ui } from '../data/ui';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeo } from '../hooks/useSeo';
export function Gallery() {
  const {
    t,
    lang
  } = useLanguage();
  useSeo({
    title: lang === 'en' ? 'Before & After Project Gallery | M&S Multiservices Dallas TX' : 'Galería de Proyectos Antes y Después | M&S Multiservices Dallas TX',
    description: lang === 'en' ? 'Real before and after photos of painting, drywall, flooring and remodeling projects completed by M&S Multiservices across Dallas-Fort Worth, Texas.' : 'Fotos reales de antes y después de proyectos de pintura, drywall, pisos y remodelación completados por M&S Multiservices en Dallas-Fort Worth, Texas.'
  });
  return <>
      <PageHeader eyebrow={t({
      en: 'Project gallery',
      es: 'Galería de proyectos'
    })} title={t({
      en: 'Our work, before and after',
      es: 'Nuestro trabajo, antes y después'
    })} description={t({
      en: 'Photos taken on our own job sites across Dallas, Denton and Tarrant counties. Drag any slider to see exactly what we started with.',
      es: 'Fotos tomadas en nuestras propias obras en los condados de Dallas, Denton y Tarrant. Desliza cualquier control para ver cómo empezamos.'
    })} data-id="element-451" data-mp="2p17zshbs4iza" />

      <section className="bg-white" aria-labelledby="all-projects" data-id="element-452" data-mp="2yxbc2g0chxzy">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20" data-id="element-453" data-mp="1ijun21sqfwh8">
          <SectionHeading id="all-projects" eyebrow={t({
          en: 'Recent projects',
          es: 'Proyectos recientes'
        })} title={t({
          en: 'Painting, drywall, flooring and full remodels',
          es: 'Pintura, drywall, pisos y remodelaciones completas'
        })} data-id="element-454" data-mp="1iiji3ynjwioj" />
          <div className="mt-12" data-id="element-455" data-mp="3kbcz1256l2sy">
            <GalleryGrid projects={galleryProjects} data-id="element-456" data-mp="3fhiho5d1szx1" />
          </div>
        </div>
      </section>

      <section className="bg-mist" aria-labelledby="featured-projects" data-id="element-457" data-mp="3bhi40mxrwsr1">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20" data-id="element-458" data-mp="36hlz8lh94xaf">
          <SectionHeading id="featured-projects" eyebrow={t({
          en: 'Featured projects',
          es: 'Proyectos destacados'
        })} title={t({
          en: 'Two jobs, start to finish',
          es: 'Dos trabajos, de principio a fin'
        })} description={t({
          en: 'The scope, the timeline and the result — so you know what to expect from a project like yours.',
          es: 'El alcance, el tiempo y el resultado — para que sepas qué esperar en un proyecto como el tuyo.'
        })} data-id="element-459" data-mp="36ix12fr4zuyw" />

          <div className="mt-12 space-y-10" data-id="element-460" data-mp="28n1ihshyxujb">
            {featuredProjects.map((project, index) => <article key={project.id} className="grid gap-8 overflow-hidden rounded-xl border border-navy/10 bg-white lg:grid-cols-2 lg:gap-0" data-id="element-461" data-mp="2dlw9hhq3apei" data-mpi={index}>
                <div className={index % 2 === 1 ? 'lg:order-2' : undefined} data-id="element-462" data-mp="2meg13y7yrtpu">
                  <BeforeAfterSlider before={project.before} after={project.after} caption={t(project.title)} data-id="element-463" data-mp="1pprudy1p7svq" />
                </div>
                <div className="p-7 sm:p-10" data-id="element-464" data-mp="1ypna8g678c23">
                  <h3 className="font-display text-2xl leading-snug text-navy sm:text-3xl" data-id="element-465" data-mp="1ys178ukfvyca">
                    {t(project.title)}
                  </h3>
                  <p className="mt-4 leading-relaxed text-ink/75" data-id="element-466" data-mp="3a5v9a7mf9590">{t(project.summary)}</p>
                  <dl className="mt-7 grid gap-5 border-t border-navy/10 pt-6 sm:grid-cols-3" data-id="element-467" data-mp="3f3di7y0fvkh0">
                    <div data-id="element-468" data-mp="3dmhk7h3r4krw">
                      <dt className="text-xs font-semibold uppercase tracking-[0.1em] text-gold-dark" data-id="element-469" data-mp="3dnsp5k8xnykl">
                        {t(ui.scope)}
                      </dt>
                      <dd className="mt-1.5 text-sm text-ink/80" data-id="element-470" data-mp="3mo7c0o1jplq7">{t(project.scope)}</dd>
                    </div>
                    <div data-id="element-471" data-mp="3ilueuo0v6w51">
                      <dt className="text-xs font-semibold uppercase tracking-[0.1em] text-gold-dark" data-id="element-472" data-mp="3ikj4g5cwyq4y">
                        {t(ui.duration)}
                      </dt>
                      <dd className="mt-1.5 text-sm text-ink/80" data-id="element-473" data-mp="3rnk5hrmx9r1r">{t(project.duration)}</dd>
                    </div>
                    <div data-id="element-474" data-mp="a4zepgbgbpun">
                      <dt className="text-xs font-semibold uppercase tracking-[0.1em] text-gold-dark" data-id="element-475" data-mp="a8df4j0k1ozg">
                        {t(ui.result)}
                      </dt>
                      <dd className="mt-1.5 text-sm text-ink/80" data-id="element-476" data-mp="j7qrbjg2crnv">{t(project.result)}</dd>
                    </div>
                  </dl>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      <CtaSection data-id="element-477" data-mp="xrux5jk67v7l" />
    </>;
}